import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Paper,
    Grid,
    Chip,
    IconButton,
    Switch,
    FormControlLabel,
    InputAdornment,
    Button,
    Collapse
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    AttachMoney as MoneyIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Session, updateSession } from 'services/sessionService';
import { useSessionStudio } from './SessionContext';

interface SessionHeaderProps {
    session: Session;
}

const SessionHeader: React.FC<SessionHeaderProps> = ({ session }) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [title, setTitle] = useState(session.title);
    const [description, setDescription] = useState(session.description || '');
    const [startDate, setStartDate] = useState(dayjs(session.startDate));
    const [endDate, setEndDate] = useState(dayjs(session.endDate));
    const [basePrice, setBasePrice] = useState(session.basePrice);
    const [acceptingEnrollment, setAcceptingEnrollment] = useState(session.acceptingEnrollment);
    const [saving, setSaving] = useState(false);
    const { refreshSession } = useSessionStudio();

    const handleSaveTitle = async () => {
        if (title !== session.title) {
            try {
                await updateSession(session.id, { title });
                await refreshSession();
            } catch (error) {
                console.error('Failed to update session title:', error);
                setTitle(session.title); // Revert on error
            }
        }
        setIsEditingTitle(false);
    };

    const handleSaveDetails = async () => {
        setSaving(true);
        try {
            await updateSession(session.id, {
                description,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                basePrice,
                acceptingEnrollment
            });
            await refreshSession();
            setIsEditingDetails(false);
        } catch (error) {
            console.error('Failed to update session:', error);
            // Revert on error
            setDescription(session.description || '');
            setStartDate(dayjs(session.startDate));
            setEndDate(dayjs(session.endDate));
            setBasePrice(session.basePrice);
            setAcceptingEnrollment(session.acceptingEnrollment);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelDetails = () => {
        setDescription(session.description || '');
        setStartDate(dayjs(session.startDate));
        setEndDate(dayjs(session.endDate));
        setBasePrice(session.basePrice);
        setAcceptingEnrollment(session.acceptingEnrollment);
        setIsEditingDetails(false);
    };

    const handleToggleEnrollment = async () => {
        const newValue = !acceptingEnrollment;
        setAcceptingEnrollment(newValue);
        try {
            await updateSession(session.id, { acceptingEnrollment: newValue });
            await refreshSession();
        } catch (error) {
            console.error('Failed to toggle enrollment:', error);
            setAcceptingEnrollment(!newValue); // Revert
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            {/* Title Section */}
            <Box sx={{ mb: 3 }}>
                {isEditingTitle ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            fullWidth
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            variant="outlined"
                            size="medium"
                            autoFocus
                        />
                        <IconButton color="primary" onClick={handleSaveTitle}>
                            <CheckIcon />
                        </IconButton>
                        <IconButton onClick={() => setIsEditingTitle(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            '&:hover .edit-icon': { opacity: 1 }
                        }}
                    >
                        <Typography variant="h4" fontWeight="bold">
                            {title}
                        </Typography>
                        <IconButton
                            className="edit-icon"
                            size="small"
                            sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                            onClick={() => setIsEditingTitle(true)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <Chip
                            label={acceptingEnrollment ? "Open for Enrollment" : "Enrollment Closed"}
                            color={acceptingEnrollment ? "success" : "default"}
                            size="small"
                            onClick={handleToggleEnrollment}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Box>
                )}
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {session.description}
                </Typography>
            </Box>

            {/* Meta Data Grid */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <CalendarIcon color="action" />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Duration
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <MoneyIcon color="action" />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Price
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                ${session.basePrice}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={isEditingDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        onClick={() => setIsEditingDetails(!isEditingDetails)}
                    >
                        {isEditingDetails ? 'Hide Details' : 'Edit Details'}
                    </Button>
                </Grid>
            </Grid>

            {/* Expanded Edit Form */}
            <Collapse in={isEditingDetails}>
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Edit Session Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(date) => date && setStartDate(date)}
                                slotProps={{
                                    textField: { fullWidth: true, size: 'small' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(date) => date && setEndDate(date)}
                                minDate={startDate}
                                slotProps={{
                                    textField: { fullWidth: true, size: 'small' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Base Price"
                                value={basePrice}
                                onChange={(e) => setBasePrice(Number(e.target.value) || 0)}
                                fullWidth
                                size="small"
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={acceptingEnrollment}
                                        onChange={(e) => setAcceptingEnrollment(e.target.checked)}
                                    />
                                }
                                label="Accepting Enrollment"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button onClick={handleCancelDetails} disabled={saving}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveDetails}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default SessionHeader;
